import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { subscribeToTable } from "@/lib/realtimeChannels";
import type { User } from "@supabase/supabase-js";

interface SubRow {
  id: string;
  status: string;
  price_id: string;
  product_id: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string;
}

export function useSubscription(user: User | null) {
  const [subscription, setSubscription] = useState<SubRow | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("subscriptions" as any)
      .select("*")
      .eq("user_id", user.id)
      .eq("environment", getStripeEnvironment())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSubscription((data as unknown as SubRow) || null);
    setLoading(false);
  };

  useEffect(() => {
    refetch();
    if (!user) return;
    const unsubscribe = subscribeToTable(
      {
        table: "subscriptions",
        event: "*",
        filter: `user_id=eq.${user.id}`,
      },
      () => refetch()
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const now = Date.now();
  const periodEndMs = subscription?.current_period_end
    ? new Date(subscription.current_period_end).getTime()
    : null;

  const isActive =
    !!subscription &&
    (
      (["active", "trialing", "past_due"].includes(subscription.status) &&
        (periodEndMs === null || periodEndMs > now)) ||
      (subscription.status === "canceled" && periodEndMs !== null && periodEndMs > now)
    );

  return { subscription, isActive, loading, refetch };
}
